import { useMachine } from '@xstate/react';
import { createContext, PropsWithChildren, useContext } from 'react';
import { Actor, StateFrom } from 'xstate';
import { GlobalSearchDialog } from '@/components/global-search/GlobalSearchDialog';
import { useGlobalSearchQueries } from '@/components/global-search/hooks/useGlobalSearchQueries';
import { useKeyboardNavigation } from '@/components/global-search/hooks/useKeyboardNavigation';
import { globalSearchMachine } from '@/components/global-search/machines/globalSearchMachine';

type GlobalSearchContextType = {
  state: StateFrom<typeof globalSearchMachine>;
  send: Actor<typeof globalSearchMachine>['send'];
};

const GlobalSearchContext = createContext<GlobalSearchContextType | null>(null);

export const GlobalSearchProvider = ({ children }: PropsWithChildren) => {
  const [state, send] = useMachine(globalSearchMachine);

  const isOpen = state.matches('open');

  useKeyboardNavigation({ isOpen, send });

  useGlobalSearchQueries({ state, send });

  return (
    <GlobalSearchContext.Provider
      value={{
        state,
        send,
      }}
    >
      {children}
      <GlobalSearchDialog state={state} send={send} />
    </GlobalSearchContext.Provider>
  );
};

export const useGlobalSearchContext = () => {
  const context = useContext(GlobalSearchContext);

  if (!context) throw new Error('useGlobalSearchContext must be used within GlobalSearchProvider.');

  return context;
};

/**
 * Returns the GlobalSearch context or `null` when rendered outside `GlobalSearchProvider`
 * (e.g. during the auth-loading state when NavBar renders before the provider is mounted).
 */
export const useOptionalGlobalSearchContext = () => {
  return useContext(GlobalSearchContext);
};
